import { Box, Flex, HStack, Heading, Icon, Stack, Text } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiFile } from "react-icons/fi";
import { StatusAccentBar } from "../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../common/components/StatusDotLabel";
import { type StatusVariant } from "../../../common/components/StatusVariant";
import { TipTap, isTiptapContentEmpty } from "../../../common/input/TipTap/TipTap";
import { useBidSubjectTerms } from "../../../common/terminology/bidSubjectTerms";

export interface ReadonlyAnswerEvidence {
  id: string;
  name: string;
}

// The read-only rendering of a single answer: a yes/no verdict, a yes/no/partly choice, free text,
// a number, or nothing answered.
export type ReadonlyAnswer =
  | { kind: "boolean"; value: boolean; comment?: string | null }
  | { kind: "choice"; value: "YES" | "NO" | "PARTIAL"; elaborationHtml?: string | null }
  | { kind: "text"; value: string }
  | { kind: "number"; value: number; unit?: string }
  | { kind: "empty" };

interface Props {
  /** Left status bar tone. Buyer views pass a verdict-derived tone; the supplier view uses neutral. */
  accent?: StatusVariant;
  /** Optional leading badge (e.g. the qualification criterion type). */
  badge?: ReactNode;
  title: string;
  /** Rendered on the title line, right after the title (e.g. the criterion's weight). */
  titleSuffix?: ReactNode;
  /**
   * "card" stands on its own; "plain" drops the frame for a card that is already inside one, such
   * as a criterion group that draws the border and the divider between its rows itself.
   */
  chrome?: "card" | "plain";
  /** Small inline label rendered before the rich-text description (e.g. "Documentation requirement"). */
  descriptionLabel?: string;
  descriptionHtml?: string | null;
  answerLabel?: string;
  answer: ReadonlyAnswer;
  notAnsweredLabel?: string;
  evidence?: ReadonlyAnswerEvidence[];
  /** Top-right slot — buyer views inject the verdict control here. */
  headerAction?: ReactNode;
  /** Below the answer block — buyer views inject the evaluation-comment field here. */
  footer?: ReactNode;
}

// Shared read-only presentation of a supplier's answer to a qualification or minimum requirement.
// Both the buyer bid-detail tabs and the supplier read-only bid page render through this component,
// so a design change lands in one place. Buyer-only affordances (verdict control, evaluation
// comment) are injected via the headerAction/footer slots rather than baked in here.
export const ReadonlyAnswerCard = ({
  accent = "neutral",
  badge,
  title,
  titleSuffix,
  chrome = "card",
  descriptionLabel,
  descriptionHtml,
  answerLabel,
  answer,
  notAnsweredLabel,
  evidence = [],
  headerAction,
  footer,
}: Props) => {
  const { t } = useTranslation();
  const bidSubjectTerms = useBidSubjectTerms();
  const framed = chrome === "card";

  return (
    <Box
      bg={framed ? "smBackground" : undefined}
      border={framed ? "1px solid" : undefined}
      borderColor={framed ? "smBorder" : undefined}
      rounded={framed ? "xl" : undefined}
      px={{ base: "4", md: "6" }}
      py="5"
      position="relative"
      overflow="hidden"
    >
      {framed && <StatusAccentBar variant={accent} />}
      <Flex align="flex-start" justify="space-between" gap="4" flexWrap="wrap" mb="4">
        <Stack spacing="1.5" minW="0">
          {badge}
          <Flex align="center" gap="2" flexWrap="wrap">
            <Heading as="h3" size="sm" color="smText">
              {title}
            </Heading>
            {titleSuffix}
          </Flex>
          {descriptionHtml && !isTiptapContentEmpty(descriptionHtml) && (
            <Box fontSize="sm" color="smMuted">
              {descriptionLabel && (
                <Text as="span" fontWeight="medium">
                  {descriptionLabel}:
                </Text>
              )}
              <TipTap content={descriptionHtml} editable={false} hasBorder={false} padding="0" maxHeight="" />
            </Box>
          )}
        </Stack>
        {headerAction}
      </Flex>

      <Stack spacing="4">
        <Stack spacing="2">
          <ReadonlyFieldLabel>{answerLabel ?? bidSubjectTerms.answer}</ReadonlyFieldLabel>
          <AnswerBlock
            answer={answer}
            notAnsweredLabel={notAnsweredLabel ?? bidSubjectTerms.notAnswered}
            framed={framed}
          />
          {evidence.length > 0 && (
            <Stack spacing="1.5">
              {evidence.map((doc) => (
                <HStack
                  key={doc.id}
                  bg="smBackground"
                  border="1px solid"
                  borderColor="smBorder"
                  rounded="md"
                  px="3"
                  py="1.5"
                  spacing="2"
                >
                  <Icon as={FiFile} color="smMuted" />
                  <Text fontSize="sm">{doc.name}</Text>
                </HStack>
              ))}
            </Stack>
          )}
        </Stack>
        {footer}
      </Stack>
    </Box>
  );
};

export const AnswerBlock = ({
  answer,
  notAnsweredLabel,
  framed,
}: {
  answer: ReadonlyAnswer;
  notAnsweredLabel: string;
  framed: boolean;
}) => {
  const { t } = useTranslation();

  // "Nothing was answered" is the one answer that needs its own frame — without it there is no
  // shape to read the absence in. Everything else is content, and inside a card that is already
  // framed by its criterion group a second box around it only adds walls.
  if (answer.kind === "empty") {
    return (
      <Box
        border="1px dashed"
        borderColor="smBorder"
        rounded="lg"
        px="3"
        py="2.5"
        fontSize="sm"
        fontStyle="italic"
        color="smMuted"
      >
        {notAnsweredLabel}
      </Box>
    );
  }

  if (answer.kind === "number") {
    const text = answer.unit ? `${answer.value} ${answer.unit}` : String(answer.value);
    if (!framed) {
      return (
        <Text fontSize="sm" fontWeight="medium" color="smText">
          {text}
        </Text>
      );
    }
    return (
      <Box
        bg="smBackgroundSecondary"
        border="1px solid"
        borderColor="smBorder"
        rounded="lg"
        px="3"
        py="2.5"
        fontSize="sm"
        fontWeight="medium"
      >
        {text}
      </Box>
    );
  }

  if (answer.kind === "choice") {
    // "Partly" is a real answer, not a shortfall, so it reads neutral rather than as an issue.
    const variant = answer.value === "YES" ? "ok" : answer.value === "NO" ? "issue" : "neutral";
    const label = answer.value === "YES" ? t("Yes") : answer.value === "NO" ? t("No") : t("Partly");
    return (
      <Box bg="smBackgroundSecondary" rounded="lg" px="4" py="3.5">
        <Stack spacing="3" align="flex-start">
          <StatusDotLabel variant={variant} label={String(label)} />
          {answer.elaborationHtml && !isTiptapContentEmpty(answer.elaborationHtml) && (
            <TipTap content={answer.elaborationHtml} editable={false} hasBorder={false} padding="0" maxHeight="" />
          )}
        </Stack>
      </Box>
    );
  }

  if (answer.kind === "text") {
    if (!framed) {
      return (
        <Text fontSize="sm" color="smText" whiteSpace="pre-wrap" maxW="82ch">
          {answer.value}
        </Text>
      );
    }
    return (
      <Box
        bg="smBackgroundSecondary"
        border="1px solid"
        borderColor="smBorder"
        rounded="lg"
        px="3"
        py="2.5"
        fontSize="sm"
        whiteSpace="pre-wrap"
      >
        {answer.value}
      </Box>
    );
  }

  return (
    <Box bg="smBackgroundSecondary" rounded="lg" px="4" py="3.5">
      <Stack spacing="3" align="flex-start">
        <StatusDotLabel variant={answer.value ? "ok" : "issue"} label={String(answer.value ? t("Yes") : t("No"))} />
        {answer.comment && (
          <Text color="smText" lineHeight="tall" whiteSpace="pre-wrap" maxW="82ch">
            {answer.comment}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export const ReadonlyFieldLabel = ({ children }: { children: ReactNode }) => (
  <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" letterSpacing="wide">
    {children}
  </Text>
);
